// src/components/projects/ProjectCard.tsx
//
// A single project. Every project on the page renders through this one
// component — the featured project is the same component with a prop set, so
// there is no duplicated markup and no per-project special case.
//
// Deliberately not interactive. These are client engagements rather than
// personal side projects, so there is no public repository or live demo to link
// to, and a "View project" button with nowhere to go would be worse than no
// action at all. The card is a static <article>.
import type { Project } from '../../config/siteConfig';
import {
  CardRoot,
  CardTitle,
  CardDescription,
  CategoryText,
  ClientName,
  Contribution,
  FeaturedBadge,
  MetaDivider,
  MetaRow,
  TechList,
  TechTag,
} from './ProjectCard.styles';

interface ProjectCardProps {
  project: Project;
  /** Wider slot and larger type. Driven by `project.featured`, never by name. */
  featured: boolean;
}

const ProjectCard = ({ project, featured }: ProjectCardProps) => {
  const { id, title, client, category, description, contribution, technologies } = project;
  const titleId = `${id}-title`;

  return (
    <CardRoot $featured={featured} aria-labelledby={titleId}>
      <MetaRow>
        <ClientName>{client}</ClientName>
        <MetaDivider aria-hidden="true">/</MetaDivider>
        <CategoryText>{category}</CategoryText>
        {featured && <FeaturedBadge>Featured</FeaturedBadge>}
      </MetaRow>

      <CardTitle id={titleId} $featured={featured}>
        {title}
      </CardTitle>

      <CardDescription>{description}</CardDescription>

      <Contribution>{contribution}</Contribution>

      {/* A list, so a screen reader announces the count rather than one
          undifferentiated run of text. The label is not visible: the list is
          metadata, and a visible heading here would compete with the title. */}
      <TechList aria-label={`Technology used in ${title}`}>
        {technologies.map((technology) => (
          <TechTag key={technology}>{technology}</TechTag>
        ))}
      </TechList>
    </CardRoot>
  );
};

export default ProjectCard;
